/**
 * 67.68: the dropship's and the pods' events, voiced (the rules and timing: `dropshipEvents.ts`). Each event is a one-shot
 * built when it fires — oscillators and noise through filters under short envelopes, into its own air low-pass and side,
 * then the SFX bus (or a sheet's destination) — and let go when it ends. Synthesized (no files):
 *   - **touch / wheels**: a low thump (a falling sine) and the ground's own noise under it (vehicleMix ROLL, by material);
 *   - **containerDown / clamps / rampDown / the shut**: metal — a thud and a few inharmonic partials ringing out;
 *   - **latch / containerLetGo / hatch**: clicks and clunks, a pod's hatch a pneumatic hiss;
 *   - **rampOpen / rampShut**: the hydraulics — a sawtooth gliding under a low-pass, a hiss over it;
 *   - **boots**: three steps a rider, two on the ramp's steel, the last on the ground;
 *   - **winch**: a motor's buzz and its gear whine, the cable's hiss, for as long as it lowers; **cableRelease**: a clunk and the
 *     cable's twang;
 *   - **warn**: a two-tone beep;
 *   - **pods**: the falling whistle (a narrow band and a tone sweeping down, rising as it comes), the impact (a deep boom, its
 *     blast and the dust settling), the hatches.
 * Cost: an event is 6–22 nodes alive for its length (0.1–3 s); a sortie's busiest moment (the riders' boots overlapping) about 40.
 * Presentation only.
 */
import type { GroundMaterial } from '../types';
import { ROLL } from './vehicleMix';
import type { DropshipEventKind } from './dropshipEvents';

/** How an event is heard at the listener: its loudness, side and the air's cut (dropshipEvents `hearDropshipFx`). */
export interface FxHeard { gain: number; pan: number; cutoff: number }

const noiseBufs = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseBuf(ctx: BaseAudioContext): AudioBuffer {
  let b = noiseBufs.get(ctx);
  if (!b) {
    const len = ctx.sampleRate * 2; b = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    noiseBufs.set(ctx, b);
  }
  return b;
}

/** A one-shot's builder: its chain to the destination, its parts, the nodes it made and when it ends. */
class Shot {
  nodes = 0;
  end: number;
  private readonly bus: GainNode;
  private lastSource: AudioScheduledSourceNode | null = null;
  private lastStop = -Infinity;
  constructor(private readonly ctx: BaseAudioContext, dest: AudioNode, h: FxHeard, readonly t0: number) {
    this.end = t0;
    const out = this.gain(h.gain), air = ctx.createBiquadFilter(), side = ctx.createStereoPanner();
    air.type = 'lowpass'; air.frequency.value = h.cutoff; air.Q.value = 0.5; side.pan.value = Math.max(-1, Math.min(1, h.pan));
    this.nodes += 2;
    this.bus = this.gain(1);
    this.bus.connect(air); air.connect(side); side.connect(out); out.connect(dest);
  }
  private gain(v: number): GainNode { const g = this.ctx.createGain(); g.gain.value = v; this.nodes++; return g; }
  /** An envelope on a fresh gain: up over `a`, held `hold`, down over `d` (exponentially), at `at` seconds after t0. */
  private env(at: number, a: number, peak: number, d: number, hold = 0): GainNode {
    const g = this.gain(0), t = this.t0 + at;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + a);
    if (hold > 0) g.gain.setValueAtTime(peak, t + a + hold);
    g.gain.exponentialRampToValueAtTime(0.0001, t + a + hold + d);
    this.end = Math.max(this.end, t + a + hold + d + 0.05);
    return g;
  }
  private run(s: AudioScheduledSourceNode, at: number, len: number): void {
    const stop = this.t0 + at + len + 0.06;
    s.start(this.t0 + at); s.stop(stop);
    if (stop > this.lastStop) { this.lastStop = stop; this.lastSource = s; }
  }
  /** A tone gliding from `f0` to `f1` Hz. */
  tone(type: OscillatorType, f0: number, f1: number, at: number, a: number, peak: number, d: number, hold = 0): void {
    const o = this.ctx.createOscillator(); o.type = type; this.nodes++;
    const t = this.t0 + at, len = a + hold + d;
    o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + len);
    o.connect(this.env(at, a, peak, d, hold)).connect(this.bus);
    this.run(o, at, len);
  }
  /** Noise through a filter (its frequency gliding from `f0` to `f1`). */
  noise(type: BiquadFilterType, f0: number, q: number, at: number, a: number, peak: number, d: number, hold = 0, f1 = f0): void {
    const s = this.ctx.createBufferSource(); s.buffer = noiseBuf(this.ctx); s.loop = true; s.loopStart = Math.random();
    const f = this.ctx.createBiquadFilter(); f.type = type; f.Q.value = q; this.nodes += 2;
    const t = this.t0 + at, len = a + hold + d;
    f.frequency.setValueAtTime(f0, t); f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + len);
    s.connect(f); f.connect(this.env(at, a, peak, d, hold)).connect(this.bus);
    this.run(s, at, len);
  }
  /** Metal ringing: a few inharmonic partials over a thud. */
  metal(at: number, partials: readonly number[], peak: number, ring: number, thudHz: number): void {
    this.tone('sine', thudHz, thudHz * 0.7, at, 0.004, peak, 0.25);
    partials.forEach((hz, i) => this.tone('sine', hz, hz * 0.995, at, 0.002, peak * 0.22 / (1 + i * 0.4), ring / (1 + i * 0.3)));
    this.noise('bandpass', 900, 3, at, 0.002, peak * 0.6, 0.07);
  }
  /** Let go of everything once it has ended. */
  finish(): number {
    if (this.lastSource) this.lastSource.onended = () => { try { this.bus.disconnect(); } catch { /* gone */ } };   // (the one that stops last)
    return this.nodes;
  }
}

/** The ground's own noise under a thump (its material's filter). */
function groundNoise(s: Shot, material: GroundMaterial, at: number, peak: number, d: number): void {
  const r = ROLL[material] ?? ROLL.soil;
  s.noise(r.type, r.hz, r.q, at, 0.003, peak * (0.5 + 0.5 * r.gain), d);
}

/** Small fixed offsets per rider or pod (the same every time). */
const jitter = (i: number, k: number) => (((i * 7919 + k * 104729) % 97) / 97 - 0.5);

/**
 * Play one event at `when` (the context's seconds), as heard (`h`). `seconds`: a long one's length; `index`: which rider
 * or pod; `material`: the ground under a thump. Returns the nodes it made.
 */
export function playDropshipFx(ctx: BaseAudioContext, dest: AudioNode, kind: DropshipEventKind, h: FxHeard, when: number, opts: { seconds?: number; index?: number; material?: GroundMaterial } = {}): number {
  const s = new Shot(ctx, dest, h, when), i = opts.index ?? 0, m = opts.material ?? 'soil', len = Math.max(0.2, opts.seconds ?? 1);
  switch (kind) {
    case 'touch':
      s.tone('sine', 64, 38, 0, 0.004, 1, 0.38); groundNoise(s, m, 0, 0.7, 0.28); s.noise('lowpass', 320, 0.7, 0, 0.004, 0.5, 0.22);
      break;
    case 'wheels':
      s.tone('sine', 52, 30, 0, 0.004, 1, 0.5); groundNoise(s, m, 0, 0.8, 0.35);
      s.tone('sawtooth', 330, 250, 0.05, 0.02, 0.08, 0.3);   // the suspension's creak
      break;
    case 'containerDown': s.metal(0, [211, 343, 557, 811], 0.8, 0.55, 88); break;
    case 'latch':
      s.noise('bandpass', 3000 + 400 * i, 5, 0, 0.001, 0.9, 0.035); s.tone('square', 1400 + 200 * i, 1300, 0, 0.001, 0.15, 0.025);
      break;
    case 'containerLetGo': s.noise('bandpass', 700, 4, 0, 0.002, 0.8, 0.12); s.tone('sine', 140, 110, 0, 0.003, 0.4, 0.16); break;
    case 'rampOpen': case 'rampShut': {
      const up = kind === 'rampShut', f0 = up ? 118 : 152, f1 = up ? 152 : 118;
      s.tone('sawtooth', f0, f1, 0, 0.08, 0.45, 0.12, len - 0.2);
      s.noise('bandpass', 3200, 2, 0, 0.1, 0.22, 0.12, len - 0.22);
      s.tone('sine', f0 * 2, f1 * 2, 0, 0.08, 0.12, 0.12, len - 0.2);
      if (up) s.metal(len, [196, 311, 487, 760], 0.8, 0.7, 72);   // shut: the clang at the end
      break;
    }
    case 'rampDown': s.metal(0, [180, 287, 451, 733, 1120], 0.85, 0.9, 70); s.noise('lowpass', 500, 0.8, 0, 0.003, 0.4, 0.2); break;
    case 'boots':
      for (let k = 0; k < 3; k++) {
        const at = k * 0.12 + 0.02 * jitter(i, k), last = k === 2;
        if (last) { s.noise('lowpass', 520, 0.8, at, 0.003, 0.6, 0.11); s.tone('sine', 80, 60, at, 0.003, 0.35, 0.07); }
        else { s.noise('bandpass', 1050 + 120 * jitter(i, k + 3), 2.5, at, 0.002, 0.75, 0.07); s.tone('sine', 98, 80, at, 0.002, 0.45, 0.06); }
      }
      break;
    case 'warn': s.tone('square', i % 2 ? 660 : 880, i % 2 ? 660 : 880, 0, 0.01, 0.32, 0.04, 0.16); break;
    case 'winch':
      s.tone('sawtooth', 84, 92, 0, 0.15, 0.4, 0.2, len - 0.35);
      s.tone('sine', 1010, 1090, 0, 0.15, 0.16, 0.2, len - 0.35);
      s.noise('bandpass', 2400, 4, 0, 0.15, 0.18, 0.2, len - 0.35);
      break;
    case 'cableRelease':
      s.noise('bandpass', 1600, 6, 0, 0.001, 0.6, 0.05); s.tone('sine', 210, 140, 0, 0.002, 0.35, 0.7); s.tone('sine', 120, 100, 0, 0.003, 0.4, 0.12);
      break;
    case 'clamps': s.metal(0, [262, 409, 688], 0.6, 0.3, 110); s.metal(0.12, [255, 402, 671], 0.5, 0.3, 105); break;
    case 'podWhistle':
      s.noise('bandpass', 2600, 8, 0, len * 0.9, 0.55, 0.05, 0, 650);
      s.tone('sine', 1800, 520, 0, len * 0.9, 0.22, 0.05);
      break;
    case 'podImpact':
      s.tone('sine', 56, 26, 0, 0.004, 1, 0.9); s.noise('lowpass', 420, 0.7, 0, 0.004, 0.85, 0.6);
      s.noise('lowpass', 1300, 0.6, 0.06, 0.08, 0.22, 1.5);   // the dust settling
      break;
    case 'podHatch': s.noise('highpass', 2100, 0.7, 0, 0.004, 0.45, 0.25); s.tone('sine', 160, 130, 0, 0.002, 0.55, 0.08); break;
  }
  return s.finish();
}
